import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Live XAUUSD / gold spot from free public APIs.
 */
export default function useLiveGoldPrice(pollMs = 30000) {
  const [price, setPrice] = useState(null);
  const [change, setChange] = useState(0);
  const [changePercent, setChangePercent] = useState(0);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const prevRef = useRef(null);

  const fetchPrice = useCallback(async () => {
    setError(null);
    const sources = [
      {
        name: 'gold-api',
        url: 'https://api.gold-api.com/price/XAU',
        get: (d) => d?.price,
      },
      {
        name: 'yahoo-GC=F',
        url: 'https://query1.finance.yahoo.com/v8/finance/chart/GC=F?interval=1d&range=2d',
        get: (d) => d?.chart?.result?.[0]?.meta?.regularMarketPrice,
      },
    ];

    let found = null;
    let used = null;
    for (const s of sources) {
      try {
        const res = await fetch(s.url, { cache: 'no-store' });
        if (!res.ok) continue;
        const data = await res.json();
        const p = Number(s.get(data));
        if (p > 0) {
          found = p;
          used = s.name;
          break;
        }
      } catch (e) {
        continue;
      }
    }

    if (found == null) {
      setError('Live price unavailable (CORS/network). Using ML file live/history.');
      setLoading(false);
      return null;
    }

    const next = parseFloat(found.toFixed(2));
    const prev = prevRef.current;
    if (prev != null && prev > 0) {
      const ch = parseFloat((next - prev).toFixed(2));
      setChange(ch);
      setChangePercent(parseFloat(((ch / prev) * 100).toFixed(2)));
    }
    prevRef.current = next;
    setPrice(next);
    setSource(used);
    setUpdatedAt(new Date());
    setLoading(false);
    return next;
  }, []);

  useEffect(() => {
    fetchPrice();
    const id = setInterval(fetchPrice, pollMs);
    return () => clearInterval(id);
  }, [fetchPrice, pollMs]);

  return { price, change, changePercent, source, loading, error, updatedAt, refresh: fetchPrice };
}
